import _ = require("underscore");
import Portal = require("portal-gis");
import Utils = require("../utils");
import MapService = require("../services/ags/map-service-reader");
import Base = require("./base/geoquery");
import FormTool = require("../controls/ags-geoquery-form-tool");

class Handler extends Base<{
    out: {
        controller: FormTool;
        command: Portal.CommandLeaf;
    }
}> {

    static about = `
Converts form input to an ArcGIS query and executes that query.  Depends on _controls/ags-geoquery-form-tool_ and _services/ags/map-service-reader_.
Requires a "url" option.

All options are forwarded to [map-service-reader](#app/services/ags/map-service-reader).
`;

    /**
     * execute extends this commands options and passes them to the
     * query method as 'options' so the options of that service need
     * to be documented here although they're nto explicitly referenced here.
     */
    static options = {
        "event": {
            "about": "event to raise when result obtained",
            "default": "geoquery-result"
        },
        "key-template": {
            "about": "underscore template for computing a feature id, inherited from invoking controller",
        },
        "returnGeometry": {
            "about": "Include the geometry in the response, forwarded to [map-service-reader](#app/services/ags/map-service-reader)",
        },
        "returnIdsOnly": {
            "about": "Only return the feature ids, forwarded to [map-service-reader](#app/services/ags/map-service-reader)",
        },
        "returnCountOnly": {
            "about": "Only return the count of matching features, forwarded to [map-service-reader](#app/services/ags/map-service-reader)",
        },
        "symbology": {
            "about": "symbology to use when rendering the results, inherited from invoking controller",
        },
        "url": {
            "about": "ags query service url, inherited from invoking controller",
        },
    }

    static output = {
        "filter": {
            "about": "The query data",
        },
        "layers": {
            "about": "The response data",
        },

        "metadata": {
            "about": "The response metadata",
        },

        "query": {
            "about": "The form data encoded as a filter",
        },

        "typeName": {
            "about": "",
        },

        "url": {
            "about": "The input endpoint",
        },
    }

    execute(args = this.args) {

        // inherits url, symbology, key-template from invoking controller
        let controllerConfig = this.controller.options;

        let eventConfig = Utils.defaults(this.asOptions(Handler.options), {
            "key-template": controllerConfig["key-template"],
            url: controllerConfig["url"],
            symbology: controllerConfig["symbology"]
        });


        Utils.ifOption(eventConfig, "url", url => {
            let data = this.asData();
            console.log("data: ", data);

            let query = this.asQuery(data);
            console.log("query: ", query);

            let filterInfo = {
                url: url,
                filter: query,
                options: eventConfig
            };

            MapService.query(filterInfo).done(result => {
                this.assignKeys(result);

                // use the form url as the symbology rule id if not explicitly specified
                Utils.extend(result, {
                    symbology: eventConfig.symbology || eventConfig.url
                });

                // TODO: this is not consistant with ips-geoquery-processor, which defers back to the controller
                // so we don't have to reach into the controller options here...look at the mess that is causing.
                this.trigger({
                    filter: data,
                    layers: [result],
                    metadata: result.fields,
                    query: query,
                    typeName: null, // ogc artifact
                    url: url
                });

            });
        });

    }

    private get controller() {
        return <FormTool>this.args.out.controller
    }

    private asData() {
        return this.controller.asData();
    }

    // creates query from nested arrays, private => untestable
    private asQuery(data: Array<{ name: string; value: any; }>, separator = " AND ") {
        return data.filter(item => !!item.value).map(item => {
            let value = item.value;
            if (typeof value === "number") {
                return `${item.name}=${value}`;
            } else if (typeof value === "string") {
                return `${item.name}='${value}'`;
            } else if (_.isArray(value)) {
                let clause = this.asQuery(value.map(v => ({ name: item.name, value: v })), " OR ");
                return `(${clause})`;
            } else {
                console.log("unsupported value type: ", value);
                return "1=1";
            }
        }).join(separator);
    }

}

export = Handler;  